import { useQuery, useMutation, useQueryClient, UseQueryOptions, UseMutationOptions } from '@tanstack/react-query';
import { FavoritesService } from '@/services/favorites';
import { 
  FavoriteWithRecipe,
  FavoritesResponse,
  ToggleFavoriteRequest,
  ToggleFavoriteResponse,
} from '@/types/api';

// Query keys for React Query
export const favoritesKeys = {
  all: ['favorites'] as const,
  lists: () => [...favoritesKeys.all, 'list'] as const,
  list: (page: number, limit: number) => [...favoritesKeys.lists(), page, limit] as const,
  count: () => [...favoritesKeys.all, 'count'] as const,
  status: (recipeId: string) => [...favoritesKeys.all, 'status', recipeId] as const,
  statuses: (recipeIds: string[]) => [...favoritesKeys.all, 'statuses', recipeIds.sort()] as const,
};

/**
 * Hook for getting user's favorite recipes (paginated)
 */
export function useFavorites(
  page: number = 1, 
  limit: number = 20,
  options?: Omit<UseQueryOptions<FavoritesResponse, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: favoritesKeys.list(page, limit),
    queryFn: () => FavoritesService.getFavorites(page, limit),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    ...options,
  });
}

/**
 * Hook for getting favorite count
 */
export function useFavoriteCount(
  options?: Omit<UseQueryOptions<number, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: favoritesKeys.count(),
    queryFn: () => FavoritesService.getFavoriteCount(),
    staleTime: 1 * 60 * 1000, // 1 minute
    gcTime: 10 * 60 * 1000, // 10 minutes
    ...options,
  });
}

/**
 * Hook for checking if a single recipe is favorited
 */
export function useFavoriteStatus(
  recipeId: string,
  options?: Omit<UseQueryOptions<boolean, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: favoritesKeys.status(recipeId),
    queryFn: () => FavoritesService.isFavorite(recipeId),
    enabled: !!recipeId,
    staleTime: 5 * 60 * 1000, // 5 minutes (increased from 30 seconds)
    gcTime: 15 * 60 * 1000, // 15 minutes (increased)
    refetchOnWindowFocus: false, // Disable refetch on focus
    refetchOnReconnect: false, // Disable refetch on reconnect
    ...options,
  });
}

/**
 * Hook for checking favorite status of multiple recipes
 */
export function useFavoriteStatuses(
  recipeIds: string[],
  options?: Omit<UseQueryOptions<Record<string, boolean>, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: favoritesKeys.statuses(recipeIds),
    queryFn: () => FavoritesService.getFavoriteStatuses({ recipeIds }),
    enabled: recipeIds.length > 0,
    staleTime: 10 * 60 * 1000, // 10 minutes (increased from 30 seconds)
    gcTime: 30 * 60 * 1000, // 30 minutes (increased)
    refetchOnWindowFocus: false, // Disable refetch on focus
    refetchOnReconnect: false, // Disable refetch on reconnect
    ...options,
  });
}

/**
 * Hook for toggling favorite status with optimistic updates
 */
type ToggleFavoriteContext = {
  previousStatus: boolean | undefined;
  previousCount: number | undefined;
};

export function useToggleFavorite(
  options?: Omit<UseMutationOptions<ToggleFavoriteResponse, Error, ToggleFavoriteRequest, ToggleFavoriteContext>, 'mutationFn' | 'onMutate' | 'onSuccess' | 'onError'>
) {
  const queryClient = useQueryClient();

  return useMutation<ToggleFavoriteResponse, Error, ToggleFavoriteRequest, ToggleFavoriteContext>({
    mutationFn: (data: ToggleFavoriteRequest) => FavoritesService.toggleFavorite(data),
    
    // Optimistic update
    onMutate: async (variables) => {
      const { recipeId } = variables;
      
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: favoritesKeys.status(recipeId) });
      await queryClient.cancelQueries({ queryKey: favoritesKeys.count() });
      
      // Snapshot previous values
      const previousStatus = queryClient.getQueryData<boolean>(favoritesKeys.status(recipeId));
      const previousCount = queryClient.getQueryData<number>(favoritesKeys.count());
      
      // Optimistically update status
      queryClient.setQueryData<boolean>(
        favoritesKeys.status(recipeId),
        (old) => !old
      );
      
      // Optimistically update count
      if (previousCount !== undefined) {
        queryClient.setQueryData<number>(
          favoritesKeys.count(),
          previousStatus ? previousCount - 1 : previousCount + 1
        );
      }
      
      // Update statuses in batch queries
      queryClient.setQueriesData<Record<string, boolean>>(
        { queryKey: favoritesKeys.all, predicate: (query) => 
          query.queryKey.includes('statuses') && 
          Array.isArray(query.queryKey[2]) && 
          (query.queryKey[2] as string[]).includes(recipeId)
        },
        (old) => old ? { ...old, [recipeId]: !old[recipeId] } : undefined
      );
      
      return { previousStatus, previousCount };
    },
    
    // On success, invalidate and refetch
    onSuccess: (data, variables) => {
      const { recipeId } = variables;
      
      // Update with actual server response
      queryClient.setQueryData<boolean>(
        favoritesKeys.status(recipeId),
        data.isFavorite
      );
      
      // Invalidate related queries to ensure consistency
      queryClient.invalidateQueries({ queryKey: favoritesKeys.lists() });
      queryClient.invalidateQueries({ queryKey: favoritesKeys.count() });
      
      // Update batch status queries
      queryClient.setQueriesData<Record<string, boolean>>(
        { queryKey: favoritesKeys.all, predicate: (query) => 
          query.queryKey.includes('statuses') && 
          Array.isArray(query.queryKey[2]) && 
          (query.queryKey[2] as string[]).includes(recipeId)
        },
        (old) => old ? { ...old, [recipeId]: data.isFavorite } : undefined
      );
    },
    
    // On error, revert optimistic update
    onError: (_error, variables, context) => {
      const { recipeId } = variables;
      
      if (context?.previousStatus !== undefined) {
        queryClient.setQueryData<boolean>(
          favoritesKeys.status(recipeId),
          context.previousStatus
        );
      }
      
      if (context?.previousCount !== undefined) {
        queryClient.setQueryData<number>(
          favoritesKeys.count(),
          context.previousCount
        );
      }
      
      // Revert batch queries
      queryClient.setQueriesData<Record<string, boolean>>(
        { queryKey: favoritesKeys.all, predicate: (query) => 
          query.queryKey.includes('statuses') && 
          Array.isArray(query.queryKey[2]) && 
          (query.queryKey[2] as string[]).includes(recipeId)
        },
        (old) => old ? { ...old, [recipeId]: context?.previousStatus ?? false } : undefined
      );
    },
    
    ...options,
  });
}

/**
 * Hook for adding a recipe to favorites
 */
export function useAddFavorite(
  options?: UseMutationOptions<FavoriteWithRecipe, Error, string>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (recipeId: string) => FavoritesService.addFavorite(recipeId),
    
    onSuccess: (_data, recipeId) => {
      // Update single status
      queryClient.setQueryData<boolean>(favoritesKeys.status(recipeId), true);
      
      // Invalidate lists and count
      queryClient.invalidateQueries({ queryKey: favoritesKeys.lists() });
      queryClient.invalidateQueries({ queryKey: favoritesKeys.count() });
      
      // Update batch status queries
      queryClient.setQueriesData<Record<string, boolean>>(
        { queryKey: favoritesKeys.all, predicate: (query) => 
          query.queryKey.includes('statuses') && 
          Array.isArray(query.queryKey[2]) && 
          (query.queryKey[2] as string[]).includes(recipeId)
        },
        (old) => old ? { ...old, [recipeId]: true } : undefined
      );
    },
    
    ...options,
  });
}

/**
 * Hook for removing a recipe from favorites
 */
export function useRemoveFavorite(
  options?: UseMutationOptions<void, Error, string>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (recipeId: string) => FavoritesService.removeFavorite(recipeId),
    
    onSuccess: (_data, recipeId) => {
      // Update single status
      queryClient.setQueryData<boolean>(favoritesKeys.status(recipeId), false);
      
      // Invalidate lists and count
      queryClient.invalidateQueries({ queryKey: favoritesKeys.lists() });
      queryClient.invalidateQueries({ queryKey: favoritesKeys.count() });
      
      // Update batch status queries
      queryClient.setQueriesData<Record<string, boolean>>(
        { queryKey: favoritesKeys.all, predicate: (query) => 
          query.queryKey.includes('statuses') && 
          Array.isArray(query.queryKey[2]) && 
          (query.queryKey[2] as string[]).includes(recipeId)
        },
        (old) => old ? { ...old, [recipeId]: false } : undefined
      );
    },
    
    ...options,
  });
}

/**
 * Hook for clearing all favorites
 */
export function useClearFavorites(
  options?: UseMutationOptions<number, Error, void>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => FavoritesService.clearFavorites(),
    
    onSuccess: () => {
      // Clear all favorites-related cache
      queryClient.invalidateQueries({ queryKey: favoritesKeys.all });
      
      // Set count to 0
      queryClient.setQueryData<number>(favoritesKeys.count(), 0);
    },
    
    ...options,
  });
}

/**
 * Hook for syncing favorites with server (useful after login/offline sync)
 */
export function useSyncFavorites(
  options?: UseMutationOptions<{
    serverFavorites: FavoriteWithRecipe[];
    conflictResolution: 'server' | 'local';
  }, Error, string[]>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (localFavoriteIds: string[]) => FavoritesService.syncFavorites(localFavoriteIds),
    
    onSuccess: () => {
      // Invalidate all favorites cache after sync
      queryClient.invalidateQueries({ queryKey: favoritesKeys.all });
    },
    
    ...options,
  });
}
